'use client';

import * as React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Video, FileText, Briefcase, Languages, PlayCircle, Search, Edit3, Phone, Building2, Bookmark, Trash2, Trash, RotateCcw, Info, ChevronDown } from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import Image from 'next/image';
import Link from 'next/link';
import { format } from 'date-fns';
import { vi, bn } from 'date-fns/locale';
import { cn, formatAddress } from '@/doi-tac-module/lib/utils';
import { useRouter, usePathname } from 'next/navigation';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { useAuth } from '@/doi-tac-module/contexts/AuthContext';
import { useNavigation } from '@/doi-tac-module/contexts/NavigationContext';
import { agencyTranslations } from '@/doi-tac-module/constants/agency-translations';
import { useToast } from "@/doi-tac-module/hooks/use-toast";
import { moveCandidateToTrash, restoreCandidateFromTrash } from "@/doi-tac-module/actions/agency-candidate-actions";
import { toggleCandidateInteraction } from "@/doi-tac-module/actions/candidate-interactions";
import { allIndustries } from '@/doi-tac-module/lib/industry-data';
import { consultants } from '@/doi-tac-module/lib/consultant-data';
import { ContactButtons } from '../contact-buttons';
import { industryTranslations } from '@/doi-tac-module/constants/industry-translations';
import { AgencyLogoHover } from '../agency-hover-card';
import { AgencyContactButtons } from '../agency-contact-buttons';
import { MediaViewer, type MediaItem } from '../image-viewer';
import { formatExperienceBadge, calculateAge } from '@/doi-tac-module/lib/experience-utils';

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

let cachedGlobalMasterPromise: Promise<any> | null = null; // Unused but kept to prevent errors if reference still exists

// --- Media Hover Content Components ---
const VideoHoverContent = React.memo(({ items, id, language, t, router, setNavigatingPath, onPlay }: any) => (
    <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-[#0D8DC8] uppercase tracking-wider flex items-center gap-2">
                <Video className="h-4 w-4" />
                {t.card?.videoTitle?.[language] || 'Video'} ({items.length})
            </h4>
            <Button
                variant="ghost"
                size="sm"
                className="h-7 text-[10px] font-bold text-[#0D8DC8] hover:bg-[#0D8DC810]"
                onClick={() => {
                    const url = `${rootPath}/${id}#videos`;
                    setNavigatingPath(url);
                    router.push(url);
                }}
            >
                {t.card?.media?.seeAll?.[language] || 'Xem tất cả'}
            </Button>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
            {items.slice(0, 9).map((item: any, i: number) => {
                const url = item.url;
                const isLastShown = i === 8 && items.length > 9;
                return (
                    <div
                        key={`vid-${item.url}-${i}`}
                        className="flex flex-col gap-1 cursor-pointer group/vid-item-container"
                        onClick={(e) => {
                            e.stopPropagation();
                            onPlay(items, i);
                        }}
                    >
                        <div className="relative aspect-square rounded-lg overflow-hidden border border-slate-100 shadow-sm group/vid-item bg-slate-900 flex items-center justify-center transition-all group-hover/vid-item-container:border-[#0D8DC8] group-hover/vid-item-container:shadow-md">
                            {url.toLowerCase().endsWith('.mp4') || url.toLowerCase().endsWith('.mov') ? (
                                <>
                                    <video src={`${url}#t=0.5`} className="object-contain w-full h-full hidden p-1" preload="metadata" muted />
                                    <img
                                        src={url.replace(/\.(mp4|mov)$/i, '_thumb.webp')}
                                        alt={item.name}
                                        className="object-contain w-full h-full opacity-90 p-1"
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                            if (e.currentTarget.previousElementSibling) {
                                                (e.currentTarget.previousElementSibling as HTMLElement).style.display = 'block';
                                            }
                                        }}
                                    />
                                </>
                            ) : (
                                <Image src={url} alt={item.name} fill className="object-contain p-1" sizes="100px" />
                            )}
                            <div className="absolute inset-0 bg-black/10 group-hover/vid-item:bg-black/0 transition-colors flex items-center justify-center">
                                <PlayCircle className="h-6 w-6 text-white/80 drop-shadow-md" />
                            </div>
                            {isLastShown && (
                                <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px] flex items-center justify-center z-10">
                                    <span className="text-white font-bold text-sm">+{items.length - 8}</span>
                                </div>
                            )}
                        </div>
                        <span className="text-[9px] text-slate-500 truncate font-medium group-hover/vid-item-container:text-[#0D8DC8] transition-colors px-1" title={item.name}>
                            {t.mediaLabels?.[item.name]?.[language] || item.name}
                        </span>
                    </div>
                );
            })}
        </div>
    </div>
));

const PhotoHoverContent = React.memo(({ items, id, language, t, router, setNavigatingPath, onOpen }: any) => (
    <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-[#F2B92A] uppercase tracking-wider flex items-center gap-2">
                <Search className="h-4 w-4" />
                {t.card?.media?.photoTitle?.[language] || 'Ảnh toàn thân'} ({items.length})
            </h4>
            <Button variant="ghost" size="sm" className="h-7 text-[10px] font-bold text-[#F2B92A] hover:bg-[#F2B92A10]" onClick={() => {
                const url = `${rootPath}/${id}#photos`;
                setNavigatingPath(url);
                router.push(url);
            }}>
                {t.card?.media?.seeDetails?.[language] || 'Xem chi tiết'}
            </Button>
        </div>
        <div className={cn("grid gap-2 text-center", items.length === 1 ? "grid-cols-1 max-w-[250px] mx-auto" : items.length === 2 ? "grid-cols-2" : "grid-cols-3")}>
            {items.slice(0, 9).map((item: any, i: number) => {
                const isLastShown = i === 8 && items.length > 9;
                return (
                    <div 
                        key={`photo-${item.url}-${i}`} 
                        className="flex flex-col gap-1 cursor-pointer group/img-item-container" 
                        onClick={(e) => { e.stopPropagation(); onOpen(items, i); }}
                    >
                        <div className="relative aspect-square rounded-lg overflow-hidden border border-slate-100 shadow-sm bg-slate-50 group/img-item transition-all group-hover/img-item-container:border-[#F2B92A] group-hover/img-item-container:shadow-md">
                            <Image src={item.url} alt={item.name} fill sizes="150px" className="object-contain p-1 transition-transform group-hover/img-item:scale-110" />
                            {isLastShown && (
                                <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px] flex items-center justify-center z-10">
                                    <span className="text-white font-bold text-sm">+{items.length - 8}</span>
                                </div>
                            )}
                        </div>
                        <span className="text-[9px] text-slate-500 truncate font-medium group-hover/img-item-container:text-[#F2B92A] transition-colors px-1" title={item.name}>
                            {t.mediaLabels?.[item.name]?.[language] || item.name}
                        </span>
                    </div>
                );
            })}
        </div>
    </div>
));

const DocHoverContent = React.memo(({ items, id, language, t, router, setNavigatingPath, onOpen }: any) => (
    <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-[#19A6DF] uppercase tracking-wider flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {t.card?.media?.docTitle?.[language] || 'Hồ sơ'} ({items.length})
            </h4>
            <Button variant="ghost" size="sm" className="h-7 text-[10px] font-bold text-[#19A6DF] hover:bg-[#19A6DF10]" onClick={() => {
                const url = `${rootPath}/${id}#documents`;
                setNavigatingPath(url);
                router.push(url);
            }}>
                {t.card?.media?.seeAll?.[language] || 'Xem tất cả'}
            </Button>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
            {items.slice(0, 9).map((item: any, i: number) => {
                const isLastShown = i === 8 && items.length > 9;
                return (
                    <div 
                        key={`doc-${item.url}-${i}`} 
                        className="flex flex-col gap-1 cursor-pointer group/doc-item-container" 
                        onClick={(e) => { e.stopPropagation(); onOpen(items, i); }}
                    >
                        <div className="relative aspect-square rounded-lg overflow-hidden border border-slate-100 shadow-sm bg-slate-50 group/doc-item transition-all group-hover/doc-item-container:border-[#19A6DF] group-hover/doc-item-container:shadow-md">
                            <Image src={item.url} alt={item.name} fill className="object-contain p-1 transition-transform group-hover/doc-item:scale-110" sizes="150px" />
                            <div className="absolute bottom-0 right-0 bg-[#19A6DF]/80 rounded-tl p-0.5"><FileText className="h-2 w-2 text-white" /></div>
                            {isLastShown && (
                                <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px] flex items-center justify-center z-10">
                                    <span className="text-white font-bold text-sm">+{items.length - 8}</span>
                                </div>
                            )}
                        </div>
                        <span className="text-[9px] text-slate-500 truncate font-medium group-hover/doc-item-container:text-[#19A6DF] transition-colors px-1" title={item.name}>
                            {t.mediaLabels?.[item.name]?.[language] || item.name}
                        </span>
                    </div>
                );
            })}
        </div>
    </div>
));

export const CandidateCard = React.memo(function CandidateCard({
    candidate,
    showExpAndLang = false,
    agencies = [],
    isTrash = false,
    isSaved = false,
    isInvited = false,
    viewMode = 'search',
    layout = 'row',
    variant = 'default',
    onInteractionChange,
    strategicPartner,
    contactPool,
    compact = false
}: {
    candidate: any,
    showExpAndLang?: boolean,
    agencies?: any[],
    strategicPartner?: any,
    contactPool?: { rule: 'random' | 'round-robin'; pool: { profile: any; weight: number }[] } | null,
    isTrash?: boolean,
    isSaved?: boolean,
    isInvited?: boolean,
    viewMode?: 'search' | 'saved' | 'invited',
    layout?: 'row' | 'grid',
    variant?: 'default' | 'matching',
    compact?: boolean,
    onInteractionChange?: (id: string, type: 'saved' | 'invited', action: 'added' | 'removed') => void
}) {
    const { language } = useLanguage();
    const { role, user } = useAuth();
    const t = agencyTranslations.candidateSearch as any;
    const cardT = t.card;

    const router = useRouter();
    const { setNavigatingPath } = useNavigation();
    const { toast } = useToast();
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [isInteracting, setIsInteracting] = React.useState<'saved' | 'invited' | null>(null);
    const [videoOpen, setVideoOpen] = React.useState(false);
    const [photoOpen, setPhotoOpen] = React.useState(false);
    const [docOpen, setDocOpen] = React.useState(false);
    const [mediaExpanded, setMediaExpanded] = React.useState(false);
    const [showCompanyWarning, setShowCompanyWarning] = React.useState(false);
    const pathname = usePathname();
    const id = candidate.id || 'UV000';

    const handleMoveToTrash = async () => {
        setIsDeleting(true);
        try {
            const res = await moveCandidateToTrash(id);
            if (res.success) {
                toast({
                    title: "Đã chuyển vào Thùng rác",
                    description: `Ứng viên ${candidate.name || id} đã được di chuyển thành công.`,
                });
                router.refresh();
            } else {
                toast({
                    variant: "destructive",
                    title: "Lỗi",
                    description: res.error || "Không thể di chuyển ứng viên.",
                });
            }
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Lỗi hệ thống",
                description: "Đã xảy ra lỗi không mong muốn.",
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleToggleInteraction = async (type: 'saved' | 'invited') => {
        setIsInteracting(type);
        try {
            const res = await toggleCandidateInteraction(id, type);
            if (res.success) {
                toast({
                    title: res.action === 'added' 
                        ? (type === 'saved' ? "Đã lưu ứng viên" : "Đã mời phỏng vấn") 
                        : (type === 'saved' ? "Đã bỏ lưu ứng viên" : "Đã hủy lời mời"),
                    description: `Ứng viên ${candidate.name || id} đã được cập nhật trạng thái.`,
                });
                if (onInteractionChange) {
                    onInteractionChange(candidate.id, type, res.action as 'added' | 'removed');
                }
            } else {
                toast({
                    variant: "destructive",
                    title: "Lỗi",
                    description: res.error || "Không thể cập nhật trạng thái.",
                });
            }
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Lỗi hệ thống",
                description: "Đã xảy ra lỗi không mong muốn.",
            });
        } finally {
            setIsInteracting(null);
        }
    };

    const handleRestoreFromTrash = async () => {
        setIsDeleting(true);
        try {
            const res = await restoreCandidateFromTrash(id);
            if (res.success) {
                toast({
                    title: "Đã khôi phục ứng viên",
                    description: `Ứng viên ${candidate.name || id} đã được đưa trở lại danh sách quản lý.`,
                });
                router.refresh();
            } else {
                toast({
                    variant: "destructive",
                    title: "Lỗi",
                    description: res.error || "Không thể khôi phục ứng viên.",
                });
            }
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Lỗi hệ thống",
                description: "Đã xảy ra lỗi không mong muốn.",
            });
        } finally {
            setIsDeleting(false);
        }
    };


    const isOwner = (role === 'ADMIN');

    const getIndustryName = (slug: string | string[]) => {
        if (!slug) return cardT.notUpdated?.[language] || 'Chưa cập nhật';
        const actualSlug = Array.isArray(slug) ? slug[0] : slug;
        if (typeof actualSlug !== 'string') return cardT.notUpdated?.[language] || 'Chưa cập nhật';
        if (t.industryNames?.[actualSlug]) return t.industryNames[actualSlug]?.[language] || actualSlug;
        const found = allIndustries.find(i => i.slug?.toLowerCase() === actualSlug.toLowerCase());
        const nameToTranslate = found ? found.name : actualSlug;
        const translation = (industryTranslations as any)[nameToTranslate];
        if (translation) return translation[language as 'vi' | 'bn'] || nameToTranslate;
        return found ? found.name : capitalize(actualSlug.replace(/-/g, ' '));
    };

    const mapLanguageLevel = (raw: string) => {
        if (!raw) return raw;
        const langMap: Record<string, { vi: string, bn: string }> = {
            'levelNone': { vi: 'Không có', bn: 'নেই' },
            'levelBasic': { vi: 'Cơ bản', bn: 'প্রাথমিক' },
            'levelIntermediate': { vi: 'Trung bình', bn: 'মাঝারি' },
            'levelGood': { vi: 'Tốt', bn: 'ভালো' },
            'levelNative': { vi: 'Bản ngữ', bn: 'মাতৃভাষা' },
            'Cơ bản': { vi: 'Cơ bản', bn: 'প্রাথমিক' },
            'Trung bình': { vi: 'Trung bình', bn: 'মাঝারি' },
            'Tốt': { vi: 'Tốt', bn: 'ভালো' },
            'Bản ngữ': { vi: 'Bản ngữ', bn: 'মাতৃভাষা' },
            'Basic': { vi: 'Cơ bản', bn: 'প্রাথমিক' },
            'Intermediate': { vi: 'Trung bình', bn: 'মাঝারি' },
            'Good': { vi: 'Tốt', bn: 'ভালো' },
            'Native': { vi: 'Bản ngữ', bn: 'মাতৃভাষা' },
        };
        const entry = langMap[raw];
        return entry ? (entry[language as 'vi' | 'bn'] ?? raw) : raw;
    };

    const getLanguageLabel = (cand: any) => {
        const lp = cand.languageProficiency || cand.japaneseLevel;
        if (!lp) return cardT.notUpdated?.[language] || 'Chưa cập nhật';
        const parts = [];
        if ((lp.jlpt && lp.jlpt !== 'None') || (lp.englishCertificate && lp.englishCertificate !== 'None')) {
            const level = lp.jlpt || lp.englishCertificate;
            const comm = mapLanguageLevel(lp.kaiwa || lp.englishCommunication || 'None');
            parts.push(`${cardT.english?.[language] || 'English'}: ${level}, ${cardT.communication?.[language] || 'Comm'}: ${comm}`);
        }
        if ((lp.vietnameseCertificate && lp.vietnameseCertificate !== 'none') ||
            (lp.vietnameseCommunication && lp.vietnameseCommunication !== 'none' && lp.vietnameseCommunication !== 'Không có')) {
            const vnComm = mapLanguageLevel(lp.vietnameseCommunication || 'None');
            parts.push(`${cardT.vietnamese?.[language] || 'Vietnamese'}: ${lp.vietnameseCertificate || 'None'}, ${cardT.communication?.[language] || 'Comm'}: ${vnComm}`);
        }
        return parts.length > 0 ? parts.join(' | ') : (cardT.notUpdated?.[language] || 'Chưa cập nhật');
    };

    const getVisaLabel = (visa: string | string[]) => {
        if (!visa) return cardT.visaTypes?.unskilled?.[language] || 'Lao động';
        const v = Array.isArray(visa) ? visa[0] : visa;
        if (v === 'thuc-tap-sinh-ky-nang' || v === 'Lao động phổ thông') return cardT.visaTypes?.unskilled?.[language] || 'Lao động';
        if (v === 'ky-nang-dac-dinh' || v === 'Lao động kỹ thuật') return cardT.visaTypes?.skilled?.[language] || 'Lao động';
        if (v === 'ky-su-tri-thuc' || v === 'Kỹ sư, chuyên gia' || v === 'Kỹ sư, trí thức' || v === 'Kỹ sư, tri thức') return cardT.visaTypes?.expert?.[language] || 'Lao động';
        return cardT.visaTypes?.unskilled?.[language] || 'Lao động';
    };

    const getVisaTextColor = (visa: string | string[]) => {
        if (!visa) return '#AFC536';
        const v = Array.isArray(visa) ? visa[0] : visa;
        if (v === 'thuc-tap-sinh-ky-nang' || v.includes('thuc-tap-sinh') || v === 'Lao động phổ thông') return '#AFC536';
        if (v === 'ky-nang-dac-dinh' || v.includes('dac-dinh') || v === 'Lao động kỹ thuật') return '#19A6DF';
        if (v === 'ky-su-tri-thuc' || v.includes('ky-su') || v === 'Kỹ sư, chuyên gia') return '#F2B92A';
        return '#AFC536';
    };

    const experienceLabel = React.useMemo(() => formatExperienceBadge(candidate, language), [candidate, language]);

    const languageLabel = React.useMemo(() => getLanguageLabel(candidate), [candidate, language]);

    let rootPath = '';
    if (variant === 'matching') rootPath = '/ung-vien';
    else if (pathname.startsWith('/doi-tac')) rootPath = '/doi-tac/ung-vien';
    else if (pathname.startsWith('/employer')) rootPath = '/employer/ung-vien';
    else if (pathname.startsWith('/ung-vien')) rootPath = '/ung-vien';
    // fallback: if rootPath still empty (e.g. on /viec-lam), default to /ung-vien
    if (!rootPath) rootPath = '/ung-vien';

    const name = candidate.name || 'Ứng viên';
    const genderRaw = candidate.gender || candidate.personalInfo?.gender || (candidate as any).sex || '';
    const gender = (genderRaw === 'Nam' || genderRaw === 'Male' || genderRaw === 'M') ? 'Nam' :
        (genderRaw === 'Nữ' || genderRaw === 'Female' || genderRaw === 'F') ? 'Nữ' : genderRaw;

    const displayAvatarUrl = React.useMemo(() => {
        const rawAvatar = candidate.avatarUrl || candidate.personalInfo?.avatarUrl;
        if (rawAvatar && rawAvatar.trim() !== "" && !rawAvatar.includes('picsum.photos')) return rawAvatar;
        const portraitFromDocs = candidate.uploadedDocuments?.["Ảnh chân dung"]?.[0]?.url ||
            candidate.uploadedDocuments?.["Ảnh chân dung (4x6)"]?.[0]?.url;
        if (portraitFromDocs) return portraitFromDocs;
        if (gender === 'Nam') return "/img/BANGLADESH/AVATAR/BLD00M.jpg";
        if (gender === 'Nữ') return "/img/BANGLADESH/AVATAR/BLD00F.jpg";
        return "/img/BANGLADESH/AVATAR/BLD000.jpg";
    }, [candidate.avatarUrl, candidate.personalInfo?.avatarUrl, candidate.uploadedDocuments, gender]);

    const visaType = getVisaLabel(candidate.desiredLaborType);
    const visaColor = getVisaTextColor(candidate.desiredLaborType);
    const currentSalary = candidate.currentSalary && candidate.currentSalary !== cardT.salary?.notFilled?.vi ? `$${Number(candidate.currentSalary).toLocaleString('en-US')}` : (cardT.notUpdated?.[language] || 'Chưa cập nhật');
    const desiredSalary = candidate.desiredSalaryVietnam ? `$${Number(candidate.desiredSalaryVietnam).toLocaleString('en-US')}` : (candidate.desiredSalary ? `$${Number(candidate.desiredSalary).toLocaleString('en-US')}` : (cardT.salary?.negotiable?.[language] || 'Negotiable'));

    const rawAddress = candidate.addressBD || candidate.currentAddress || candidate.address || (cardT.notUpdated?.[language] || 'Chưa cập nhật');
    const formattedAdr = (rawAddress !== (cardT.notUpdated?.[language] || 'Chưa cập nhật') && typeof rawAddress === 'string') ? (formatAddress(rawAddress) || rawAddress) : rawAddress;
    const locationStr = (typeof formattedAdr === 'string' && formattedAdr.length > 25) ? formattedAdr.substring(0, 25) + '...' : formattedAdr;

    const currentLocale = language === 'bn' ? bn : vi;
    const formattedDate = candidate.createdDate
        ? format(new Date(candidate.createdDate), 'HH:mm:ss dd/MM/yyyy', { locale: currentLocale })
        : format(new Date(), 'dd/MM/yyyy', { locale: currentLocale });

    const getThumbUrl = (url: string) => {
        if (!url || typeof url !== 'string') return url;
        if (url.includes('/img/BANGLADESH/') && url.match(/\.(png|jpe?g)$/i)) {
            return url.replace(/\.(png|jpe?g)$/i, '_thumb.webp');
        }
        if (url.includes('/img/BANGLADESH/') && url.match(/\.(mp4|mov)$/i)) {
            return url.replace(/\.(mp4|mov)$/i, '_thumb.webp');
        }
        return url;
    };

    let videoItems: any[] = [];
    if (Array.isArray(candidate.videoThumbnails)) {
        candidate.videoThumbnails.forEach((v: any) => { if (v.url) videoItems.push({ url: v.url, name: v.name || 'Video' }); });
    }
    if (candidate.uploadedVideos) {
        Object.entries(candidate.uploadedVideos).forEach(([key, arr]: [string, any]) => {
            if (Array.isArray(arr)) arr.forEach((v) => { if (v.url && !videoItems.some(item => item.url === v.url)) videoItems.push({ url: v.url, name: key }); });
        });
    }

    let photoItems: any[] = [];
    if (candidate.uploadedDocuments) {
        const targetPhotoKeys = ["Ảnh toàn thân", "Ảnh toàn thân trước", "Ảnh toàn thân phải", "Ảnh toàn thân trái"];
        targetPhotoKeys.forEach(key => {
            const docs = candidate.uploadedDocuments[key];
            if (Array.isArray(docs) && docs.length > 0 && docs[0].url) {
                photoItems.push({ url: docs[0].url, name: key });
            }
        });
    }

    const ALLOWED_DOCUMENT_KEYS = [
        "Thẻ ngoại kiều 2 mặt",
        "Ảnh 3 kyu",
        "Ảnh Senmonkyu",
        "Hộ chiếu các mặt có tem, có dấu",
        "Giấy đánh giá (Hyoka Chōsho - Dành cho các bạn trượt kỳ thi năm 3 Thực tập sinh)",
        "Giấy kết thúc hoàn thành Thực tập sinh",
        "Chứng chỉ Tokutei Gino 1 phù hợp",
        "Chứng chỉ Tokutei Gino 2 phù hợp",
        "Chứng chỉ tiếng Nhật JLPT",
        "Chứng chỉ tiếng Nhật JFT A2",
        "Cv gốc (sơ yếu lý lịch hồi đi Nhật cty phái cử khai cho các bạn)",
        "Bằng lái xe AT",
        "Bằng lái xe MT",
        "Bằng lái xe Junchu",
        "Bằng lái xe Chugata",
        "Bằng lái xe Ogata",
        "Hộ chiếu các trang",
        "Thẻ ID quốc gia (NID)",
        "Lý lịch tư pháp",
        "Giấy khám sức khỏe",
        "Bằng cấp/Chứng chỉ cao nhất"
    ];

    let documentItems: any[] = [];
    if (candidate.uploadedDocuments) {
        Object.entries(candidate.uploadedDocuments).forEach(([key, arr]: [string, any]) => {
            if (ALLOWED_DOCUMENT_KEYS.includes(key) && Array.isArray(arr)) {
                arr.forEach((doc) => {
                    if (doc.url && !documentItems.some(item => item.url === doc.url)) {
                        documentItems.push({ url: doc.url, name: key });
                    }
                });
            }
        });
    }

    const [galleryState, setGalleryState] = React.useState<{ isOpen: boolean, index: number, items: MediaItem[] }>({
        isOpen: false,
        index: 0,
        items: []
    });

    const openGallery = React.useCallback((items: any[], index: number) => {
        const mappedItems: MediaItem[] = items.map(item => ({
            src: item.url,
            alt: item.name || 'Media',
            type: (item.url.toLowerCase().endsWith('.mp4') || item.url.toLowerCase().endsWith('.mov')) ? 'video' : 'image',
            candidateName: name,
            candidateId: id
        }));
        setGalleryState({ isOpen: true, index, items: mappedItems });
    }, [name, id]);

    const agencyId = (candidate as any).bangladeshAgency || (candidate as any).consultant?.id || (candidate as any).agencyId;
    const preloadedAgency = React.useMemo(() => {
        if (!agencyId || !agencies) return null;
        return agencies.find(a => a.uid === agencyId);
    }, [agencyId, agencies]);

    const fallbackConsultantData = React.useMemo(() => {
        let sum = 0;
        const idStr = id || 'UV000';
        for (let i = 0; i < idStr.length; i++) sum += idStr.charCodeAt(i);
        return consultants[sum % consultants.length];
    }, [id]);

    const isAgencyCandidatesPage = pathname === '/doi-tac/ung-vien';

    // Pick master per-card using candidate.id as deterministic seed
    const pickedMaster = React.useMemo(() => {
        if (isAgencyCandidatesPage) return null;
        if (!contactPool || contactPool.pool.length === 0) return strategicPartner || null;
        if (contactPool.pool.length === 1) return contactPool.pool[0].profile;

        // Hash của candidate.id làm seed
        let hash = 0;
        const idStr = id || 'UV000';
        for (let i = 0; i < idStr.length; i++) {
            hash = (hash * 31 + idStr.charCodeAt(i)) >>> 0;
        }

        if (contactPool.rule === 'round-robin') {
            return contactPool.pool[hash % contactPool.pool.length].profile;
        }

        // Weighted random by hash
        const total = contactPool.pool.reduce((s, m) => s + m.weight, 0);
        let pos = hash % total;
        for (const m of contactPool.pool) {
            pos -= m.weight;
            if (pos < 0) return m.profile;
        }
        return contactPool.pool[contactPool.pool.length - 1].profile;
    }, [id, isAgencyCandidatesPage, contactPool, strategicPartner]);

    const displayConsultant = pickedMaster || candidate.consultant || fallbackConsultantData;

    const isPendingRemoval = (viewMode === 'saved' && !isSaved) || (viewMode === 'invited' && !isInvited);

    return (
        <div id={id} className={cn(
            "w-full transition-all duration-300 rounded-lg border bg-card text-card-foreground relative",
            isTrash ? "grayscale opacity-80" : "hover:shadow-lg",
            isPendingRemoval && "opacity-50 grayscale bg-slate-50/50 scale-[0.98]"
        )}>
            <div className="p-3 hover:bg-secondary/30">
                {(isAgencyCandidatesPage || isTrash) && isOwner && (
                    <div className="hidden md:flex absolute top-4 right-4 items-center gap-2 group/action-buttons z-20">
                        {isTrash ? (
                            <>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="bg-white/80 backdrop-blur-sm rounded-full h-10 w-10 text-red-600 hover:text-white hover:bg-red-600 shadow-md border border-red-200 transition-all"
                                    onClick={(e) => { e.stopPropagation(); }}
                                    title="Xoá vĩnh viễn"
                                >
                                    <Trash className="h-5 w-5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="bg-white/80 backdrop-blur-sm rounded-full h-10 w-10 text-green-600 hover:text-white hover:bg-green-600 shadow-md border border-green-200 transition-all"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRestoreFromTrash();
                                    }}
                                    disabled={isDeleting}
                                    title="Khôi phục"
                                >
                                    <RotateCcw className="h-5 w-5" />
                                </Button>
                            </>
                        ) : (
                            <>
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="bg-white/80 backdrop-blur-sm rounded-full h-10 w-10 text-red-500 hover:text-white hover:bg-red-500 shadow-md border border-red-200 transition-all opacity-0 group-hover/action-buttons:opacity-100"
                                            onClick={(e) => { e.stopPropagation(); }}
                                            title={agencyTranslations.imageActions.delete[language] || 'Xóa'}
                                        >
                                            <Trash2 className="h-5 w-5" />
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Xác nhận xóa</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                Bạn có chắc chắn muốn chuyển ứng viên này vào Thùng rác không?
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Hủy</AlertDialogCancel>
                                            <AlertDialogAction
                                                className="bg-red-500 hover:bg-red-600"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleMoveToTrash();
                                                }}
                                                disabled={isDeleting}
                                            >
                                                {isDeleting ? "Đang xử lý..." : "Xác nhận"}
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>

                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="bg-white/80 backdrop-blur-sm rounded-full h-10 w-10 text-[#0D8DC8] hover:text-white hover:bg-[#0D8DC8] shadow-md border border-[#0D8DC8]/30 transition-all"
                                    onClick={(e) => { 
                                        e.stopPropagation(); 
                                        const url = `/doi-tac/update-candidate/${id}`;
                                        setNavigatingPath(url);
                                        router.push(url); 
                                    }}
                                    title={cardT.buttons?.edit?.[language] || 'Sửa'}
                                >
                                    <Edit3 className="h-5 w-5" />
                                </Button>
                            </>
                        )}
                    </div>
                )}
                <div className={cn(
                    "flex flex-col items-stretch gap-4 relative",
                    layout !== 'grid' && "md:flex-row"
                )}>
                    {/* Top-right corner — variant='matching': single [Lưu], else: single [Lưu] (mobile-only for row) */}
                    {variant === 'matching' ? (
                        <div className="absolute top-2 right-2 flex flex-col gap-1 z-20">
                            <Button
                                variant="outline"
                                size="sm"
                                className={cn(
                                    "bg-white/90 backdrop-blur-sm rounded-lg h-8 px-2.5 text-muted-foreground shadow-sm border transition-all flex items-center gap-1",
                                    isSaved ? "border-[#F2B92A] bg-[#F2B92A10] text-[#F2B92A]" : "border-slate-200"
                                )}
                                onClick={(e) => { e.stopPropagation(); handleToggleInteraction('saved'); }}
                                disabled={isInteracting === 'saved'}
                            >
                                <Bookmark className={cn("h-3.5 w-3.5", isSaved && "fill-[#F2B92A] text-[#F2B92A]")} />
                                <span className="text-[11px] font-medium">
                                    {isSaved ? (language === 'vi' ? "Đã lưu" : "সংরক্ষিত") : (cardT.buttons?.save?.[language] || "Lưu")}
                                </span>
                            </Button>
                        </div>
                    ) : (
                        <Button
                            variant="outline"
                            size="sm"
                            className={cn(
                                "bg-white/90 backdrop-blur-sm rounded-lg h-9 px-3 text-muted-foreground z-20 shadow-sm border transition-all flex items-center gap-1.5",
                                layout === 'grid' ? "absolute top-2 right-2" : "absolute top-2 right-2 md:hidden",
                                isSaved ? "border-[#F2B92A] bg-[#F2B92A10] text-[#F2B92A]" : "border-slate-200"
                            )}
                            onClick={(e) => { 
                                e.stopPropagation(); 
                                handleToggleInteraction('saved'); 
                            }}
                            disabled={isInteracting === 'saved'}
                        >
                            <Bookmark className={cn("h-4 w-4", isSaved && "fill-[#F2B92A] text-[#F2B92A]")} />
                            <span className="text-[12px] font-medium">
                                {isSaved ? (language === 'vi' ? "Đã lưu" : "সংরক্ষিত") : (cardT.buttons?.save?.[language] || "Lưu")}
                            </span>
                        </Button>
                    )}
                    <Link
                        href={`${rootPath}/${id}`}
                        onClick={() => setNavigatingPath(`${rootPath}/${id}`)}
                        className={cn(
                            "relative w-full flex-shrink-0 flex flex-col items-center justify-center bg-slate-50/50 rounded-lg border border-slate-100 gap-2 py-4 self-stretch cursor-pointer group/avatar transition-all hover:bg-slate-100 hover:border-primary/20",
                            layout !== 'grid' && "md:w-56"
                        )}
                    >
                        <Avatar className={cn("ring-4 ring-white shadow-md rounded-full", layout === 'grid' ? "h-24 w-24" : "h-32 w-32")}>
                            <AvatarImage src={displayAvatarUrl} alt={name} className="object-cover rounded-full" />
                            <AvatarFallback className="bg-primary/5 text-primary text-3xl font-bold" />
                        </Avatar>
                        <span className="text-[12px] font-headline font-bold text-slate-500 bg-white border border-slate-200 rounded-full px-3 py-1 shadow-sm flex items-center gap-1.5">
                            <Image src="/img/BANGLADESH/20. Bangladesh.png" alt="BD Flag" width={16} height={11} className="rounded-sm shrink-0" />
                            {id}
                        </span>
                    </Link>

                    <div className="flex flex-grow flex-col justify-center text-left min-w-0">
                        <Link 
                            href={`${rootPath}/${id}`} 
                            onClick={() => setNavigatingPath(`${rootPath}/${id}`)}
                            className="hover:text-primary transition-colors group mb-2 block min-w-0"
                        >
                            <h3 className={cn(
                                compact ? "text-base font-bold leading-snug" : "text-lg font-bold leading-tight",
                                layout === 'grid' 
                                    ? "block line-clamp-2" 
                                    : "inline-flex flex-wrap items-center gap-x-2 gap-y-1"
                            )}>
                                <span>{name}, {getIndustryName(candidate.desiredIndustry)}, {gender === 'Nam' ? (language === 'bn' ? 'पुरुष' : 'Nam') : (language === 'bn' ? 'নারী' : 'Nữ')}, {calculateAge(candidate)} {cardT.age?.[language] || 'tuổi'}, {candidate.height || '?'}cm, {candidate.weight || '?'}kg</span>
                            </h3>
                        </Link>

                        <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                            <Badge variant="outline" className="font-bold" style={{ color: visaColor, borderColor: `${visaColor}40`, backgroundColor: `${visaColor}08` }}>{visaType}</Badge>
                            <Badge variant="secondary" className="border-green-200 bg-green-100 text-xs text-green-800">{cardT.salary?.current?.[language] || 'Lương'} {currentSalary}</Badge>
                            <Badge variant="secondary" className="border-primary/20 bg-primary/5 text-xs text-primary font-bold">{cardT.salary?.desired?.[language] || 'Lương mong muốn'} {desiredSalary}</Badge>
                        </div>

                        {showExpAndLang && (
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 text-sm text-slate-600">
                                <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded border border-slate-100">
                                    <Briefcase className="h-3.5 w-3.5 text-slate-400" />
                                    <span className="font-medium text-[13px] text-slate-900">{experienceLabel}</span>
                                </div>
                                <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded border border-slate-100">
                                    <Languages className="h-3.5 w-3.5 text-slate-400" />
                                    <span className="font-medium text-[13px] text-slate-900">{languageLabel}</span>
                                </div>
                            </div>
                        )}

                        {(() => {
                            const mediaNodes = [];

                            if (videoItems.length > 0) {
                                mediaNodes.push(
                                    <HoverCard 
                                        key="video"
                                        open={videoOpen} 
                                        onOpenChange={(open) => {
                                            setVideoOpen(open);
                                        }}
                                        openDelay={100}
                                    >
                                        <HoverCardTrigger asChild>
                                            <div
                                                className="flex items-center gap-1.5 bg-[#0D8DC808] border border-[#0D8DC820] rounded-lg px-2 py-1 cursor-pointer hover:bg-[#0D8DC810]"
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    if (window.innerWidth < 768) {
                                                        setVideoOpen(true);
                                                    } else {
                                                        const url = `${rootPath}/${id}#videos`;
                                                        setNavigatingPath(url);
                                                        router.push(url); 
                                                    }
                                                }}
                                            >
                                                <div className="flex -space-x-1 ml-0.5">
                                                    {videoItems.slice(0, 3).map((item: any, i: number) => (
                                                        <div key={`v-thumb-${item.url || 'na'}-${i}`} className="relative h-7 w-7 aspect-square rounded-lg overflow-hidden border-2 border-white shadow-sm bg-slate-50">
                                                            <img src={getThumbUrl(item.url)} alt="" className="object-cover w-full h-full p-0.5" />
                                                            <div className="absolute inset-0 bg-white/20 flex items-center justify-center">
                                                                <Video className="h-2.5 w-2.5 text-primary" />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                {videoItems.length > 3 && (
                                                    <span className="text-[10px] font-bold text-[#0D8DC8]">+{videoItems.length - 3}</span>
                                                )}
                                                <span className="text-[10px] font-semibold text-[#0D8DC8] ml-0.5">{videoItems.length} {cardT.media?.video?.[language] || 'video'}</span>
                                            </div>
                                        </HoverCardTrigger>
                                        <HoverCardContent 
                                            className="w-[calc(100vw-32px)] sm:w-[360px] max-w-[450px] p-4 border-[#0D8DC820] shadow-2xl rounded-xl bg-white/95 backdrop-blur-md z-[100]" 
                                            side="top" 
                                            align="center" 
                                            sideOffset={10}
                                        >
                                            <VideoHoverContent items={videoItems} id={id} language={language} t={cardT} router={router} setNavigatingPath={setNavigatingPath} onPlay={openGallery} />
                                        </HoverCardContent>
                                    </HoverCard>
                                );
                            }

                            if (photoItems.length > 0) {
                                mediaNodes.push(
                                    <HoverCard 
                                        key="photo"
                                        open={photoOpen} 
                                        onOpenChange={setPhotoOpen}
                                        openDelay={100}
                                    >
                                        <HoverCardTrigger asChild>
                                            <div
                                                className="flex items-center gap-1.5 bg-[#F2B92A08] border border-[#F2B92A20] rounded-lg px-2 py-1 cursor-pointer hover:bg-[#F2B92A10]"
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    if (window.innerWidth < 768) {
                                                        setPhotoOpen(true);
                                                    } else {
                                                        const url = `${rootPath}/${id}#photos`;
                                                        setNavigatingPath(url);
                                                        router.push(url); 
                                                    }
                                                }}
                                            >
                                                <div className="flex -space-x-1 ml-0.5">
                                                    {photoItems.slice(0, 3).map((item: any, i: number) => (
                                                        <div key={`p-thumb-${item.url || 'na'}-${i}`} className="relative h-7 w-7 aspect-square rounded-lg overflow-hidden border-2 border-white shadow-sm bg-slate-50">
                                                            <img src={getThumbUrl(item.url)} alt="" className="object-cover w-full h-full p-0.5" />
                                                        </div>
                                                    ))}
                                                </div>
                                                {photoItems.length > 3 && (
                                                    <span className="text-[10px] font-bold text-[#F2B92A]">+{photoItems.length - 3}</span>
                                                )}
                                                <span className="text-[10px] font-semibold text-[#F2B92A] ml-0.5">{photoItems.length} {cardT.media?.photo?.[language] || 'ảnh'}</span>
                                            </div>
                                        </HoverCardTrigger>
                                        <HoverCardContent 
                                            className="w-[calc(100vw-32px)] sm:w-[520px] max-w-[600px] p-4 border-[#F2B92A20] shadow-2xl rounded-xl bg-white/95 backdrop-blur-md z-[100]" 
                                            side="top" 
                                            align="center" 
                                            sideOffset={10}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <PhotoHoverContent items={photoItems} id={id} language={language} t={cardT} router={router} setNavigatingPath={setNavigatingPath} onOpen={openGallery} />
                                        </HoverCardContent>
                                    </HoverCard>
                                );
                            }

                            if (documentItems.length > 0) {
                                mediaNodes.push(
                                    <HoverCard 
                                        key="doc"
                                        open={docOpen} 
                                        onOpenChange={setDocOpen}
                                        openDelay={100}
                                    >
                                        <HoverCardTrigger asChild>
                                            <div
                                                className="flex items-center gap-1.5 bg-[#19A6DF08] border border-[#19A6DF20] rounded-lg px-2 py-1 cursor-pointer hover:bg-[#19A6DF10]"
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    if (window.innerWidth < 768) {
                                                        setDocOpen(true);
                                                    } else {
                                                        const url = `${rootPath}/${id}#documents`;
                                                        setNavigatingPath(url);
                                                        router.push(url); 
                                                    }
                                                }}
                                            >
                                                <div className="flex -space-x-1 ml-0.5">
                                                    {documentItems.slice(0, 3).map((item: any, i: number) => (
                                                        <div key={`d-thumb-${item.url || 'na'}-${i}`} className="relative h-7 w-7 aspect-square rounded-lg overflow-hidden border-2 border-white shadow-sm bg-slate-50">
                                                            <Image src={getThumbUrl(item.url)} alt="" fill className="object-cover" sizes="28px" />
                                                            <div className="absolute bottom-0 right-0 bg-[#19A6DF]/80 rounded-tl p-px">
                                                                <FileText className="h-2 w-2 text-white" />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                {documentItems.length > 3 && (
                                                    <span className="text-[10px] font-bold text-[#19A6DF]">+{documentItems.length - 3}</span>
                                                )}
                                                <span className="text-[10px] font-semibold text-[#19A6DF] ml-0.5">{documentItems.length} {cardT.media?.docs?.[language] || 'hồ sơ'}</span>
                                            </div>
                                        </HoverCardTrigger>
                                        <HoverCardContent 
                                            className="w-[calc(100vw-32px)] sm:w-[520px] max-w-[600px] p-4 border-[#19A6DF20] shadow-2xl rounded-xl bg-white/95 backdrop-blur-md z-[100]" 
                                            side="top" 
                                            align="center" 
                                            sideOffset={10}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <DocHoverContent items={documentItems} id={id} language={language} t={cardT} router={router} setNavigatingPath={setNavigatingPath} onOpen={openGallery} />
                                        </HoverCardContent>
                                    </HoverCard>
                                );
                            }

                            if (mediaNodes.length === 0) return null;

                            const hasMultiple = mediaNodes.length > 1;
                            const showNodes = (layout === 'grid' && !mediaExpanded && hasMultiple) 
                                ? [mediaNodes[0]] 
                                : mediaNodes;

                            return (
                                <div className="flex flex-wrap items-center gap-3 mb-3">
                                    {showNodes}
                                    {layout === 'grid' && hasMultiple && (
                                        <button
                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMediaExpanded(!mediaExpanded); }}
                                            className="flex items-center justify-center p-1.5 rounded-full bg-slate-100/80 text-slate-500 hover:bg-slate-200 hover:text-primary transition-colors border border-slate-200/50"
                                            title={mediaExpanded ? "Thu gọn bớt" : `Xem thêm ${mediaNodes.length - 1} mục khác`}
                                        >
                                            <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", mediaExpanded && "rotate-180")} />
                                        </button>
                                    )}
                                </div>
                            );
                        })()}

                        {!compact && (
                            <div className="text-sm text-muted-foreground mb-1">
                                <p className="flex items-center gap-1.5">
                                    <MapPin className="h-4 w-4 shrink-0" />
                                    <span className="truncate max-w-[300px]" title={String(formattedAdr)}>{locationStr}</span>
                                </p>
                            </div>
                        )}

                        <div className="mt-auto flex items-end justify-between gap-2 pt-1 w-full min-w-0">
                            <div className="flex items-center gap-1.5 min-w-0 shrink">
                                <AgencyContactButtons
                                    agencyId={agencyId}
                                    preloadedAgency={pickedMaster ? null : preloadedAgency}
                                    displayConsultant={displayConsultant}
                                    language={language}
                                    showChatText={variant !== 'matching'}
                                />
                            </div>
                            <div className={cn("flex items-center gap-2", variant === 'matching' ? "flex-1 justify-end min-w-[64px]" : "shrink-0")}>
                                {variant !== 'matching' && (
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        className={cn(
                                            "hidden bg-white sm:flex border-gray-300 transition-all",
                                            isSaved ? "border-[#F2B92A] bg-[#F2B92A10] text-[#F2B92A] hover:bg-[#F2B92A20]" : "hover:bg-slate-50 hover:text-black text-slate-600"
                                        )}
                                        onClick={(e) => { e.stopPropagation(); handleToggleInteraction('saved'); }}
                                        disabled={isInteracting === 'saved'}
                                    >
                                        <Bookmark className={cn("mr-1.5 h-3.5 w-3.5", isSaved ? "fill-[#F2B92A] text-[#F2B92A]" : "text-gray-400")} />
                                        {isSaved ? "Đã lưu" : cardT.buttons.save?.[language] || 'Lưu'}
                                    </Button>
                                )}
                                <Button 
                                    size="sm" 
                                    className={cn(
                                        "text-white transition-all truncate",
                                        variant === 'matching' ? "px-2.5 h-8 text-[11px] font-medium rounded-lg w-full flex-1 max-w-[90px]" : "",
                                        isInvited ? "bg-[#AFC536] hover:bg-[#9cb131]" : "bg-accent-orange hover:bg-[#d97706]"
                                    )}
                                    onClick={(e) => { 
                                        e.stopPropagation(); 
                                        /* MOIPHONGVAN001: Require Company Name before Inviting (not when cancelling) */
                                        if (role === 'company' && !user?.agencyProfile?.companyName && !isInvited) {
                                            setShowCompanyWarning(true);
                                            return;
                                        }
                                        handleToggleInteraction('invited'); 
                                    }}
                                    disabled={isInteracting === 'invited'}
                                >
                                    {variant === 'matching' 
                                        ? (isInvited ? "Đã mời" : "Mời") 
                                        : (isInvited ? "Đã mời phỏng vấn" : cardT.buttons?.interviewShort?.[language] || 'Mời PV')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="w-full px-3 pb-1 mt-1">
                <p className="flex items-center justify-end gap-1.5 text-right w-full text-[11px] text-[#9B999A]">
                    <span className='text-primary'>{cardT.registeredAt?.[language] || 'Đăng ký lúc'}</span>
                    <span>{formattedDate}</span>
                </p>
            </div>

            <MediaViewer 
                isOpen={galleryState.isOpen}
                onOpenChange={(open) => setGalleryState(prev => ({ ...prev, isOpen: open }))}
                items={galleryState.items}
                currentIndex={galleryState.index}
                onIndexChange={(idx) => setGalleryState(prev => ({ ...prev, index: idx }))}
            />

            {/* MOIPHONGVAN001: Dialog cảnh báo thiếu Tên pháp nhân */}
            <AlertDialog open={showCompanyWarning} onOpenChange={setShowCompanyWarning}>
                <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-slate-900">
                            <Info className="w-5 h-5 text-[#19A6DF]" />
                            Yêu cầu bổ sung thông tin
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-600 leading-relaxed">
                            Để bắt đầu gửi lời mời phỏng vấn chính thức, bạn vui lòng hoàn thiện <strong>Tên pháp nhân công ty</strong>. Việc này giúp định danh doanh nghiệp và tăng mức độ tin cậy đối với các ứng viên tiềm năng.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={(e) => { e.stopPropagation(); setShowCompanyWarning(false); }}>Để sau</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-[#19A6DF] hover:bg-[#158bbd] text-white"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowCompanyWarning(false);
                                const url = `/cap-nhat-ho-so?inviteMode=true&candidateId=${id}`;
                                router.push(url);
                            }}
                        >
                            Đi đến cập nhật
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
});
